import { isDefined } from 'fmrs';
import { useCallback, useLayoutEffect, useMemo } from 'react';
import RequiredMap from '@utils/required-map';
import validateGroups from '@utils/validate-groups';

// CSS module identifiers are in the form `._${name}_${hash}`.
const CSS_MODULE_IDENTIFIERS =
  /[.#](?<identifier>_(?<name>[A-Za-z\d-]+)_[^,:.#>+\s{[]+)/gmu;

export default function useInlineStyles(
  inlineStyles: string
): (
  classNamesRecord: Partial<Readonly<Record<string, boolean>>>,
  ...classNames: readonly (string | undefined)[]
) => string {
  useLayoutEffect((): VoidFunction => {
    const style: HTMLStyleElement = window.document.createElement('style');
    style.appendChild(window.document.createTextNode(inlineStyles));
    window.document.head.appendChild(style);

    return (): void => {
      window.document.head.removeChild(style);
    };
  }, [inlineStyles]);

  // The identifier map maps the human-readable <name> to the runtime <hash>.
  const identifierMap = useMemo((): RequiredMap<string, string> => {
    const identifierMap = new RequiredMap<string, string>();

    for (const { groups } of inlineStyles.matchAll(CSS_MODULE_IDENTIFIERS)) {
      const { identifier, name } = validateGroups(groups, [
        'identifier',
        'name',
      ]);

      identifierMap.set(name, identifier);
    }

    return identifierMap;
  }, [inlineStyles]);

  return useCallback(
    (
      classNamesRecord: Partial<Readonly<Record<string, boolean>>>,
      ...classNames: readonly (string | undefined)[]
    ): string => {
      const classNamesArray: string[] = [...classNames].filter(isDefined);

      for (const [className, applied] of Object.entries(classNamesRecord)) {
        if (!applied) {
          continue;
        }

        classNamesArray.push(identifierMap.get(className));
      }

      return classNamesArray.join(' ');
    },
    [identifierMap]
  );
}
